import React, { useState, useCallback, useEffect } from 'react';
import { Brain, Play, Volume2, VolumeX, RotateCcw, Save, BookOpen } from 'lucide-react';
import { flashCardLanguages, flashCardDifficulties, flashCardSettings } from '../data/flashCardsData';
import deepseekApi from '../services/deepseekApi';
import FlashCardsFallback from './FlashCardsFallback';
import FlashCardWithAnimation from './FlashCardWithAnimation';
import flashCardStorage from '../services/flashCardStorage';
import { renderMathJax } from '../utils/mathJaxUtils';

const FlashCardsGenerator = ({ onBack, selectedTopic = null }) => {
  const [step, setStep] = useState('setup'); // setup, generating, playing, results
  const [formData, setFormData] = useState({
    topic: selectedTopic ? selectedTopic.title : '',
    numberOfCards: flashCardSettings.defaultNumberOfCards,
    language: 'Traditional Chinese',
    difficulty: 'intermediate',
    useTopicContent: !!selectedTopic
  });
  const [flashCards, setFlashCards] = useState(null);
  const [error, setError] = useState('');
  const [useAI, setUseAI] = useState(true);

  // Initialize MathJax when component mounts or updates
  useEffect(() => {
    renderMathJax(150);
  }, [step, flashCards]);
  const [savedCards, setSavedCards] = useState(new Set());

  // Initialize saved cards on component mount
  React.useEffect(() => {
    if (flashCards && flashCards.cards) {
      const savedCardIds = new Set();
      flashCards.cards.forEach(card => {
        const cardId = card.id || flashCardStorage.generateCardId(card);
        if (flashCardStorage.isCardSaved(cardId)) {
          savedCardIds.add(cardId);
        }
      });
      setSavedCards(savedCardIds);
    }
  }, [flashCards]);

  const handleGenerate = async () => {
    if (!formData.topic.trim()) {
      setError('Please enter a topic for your flash cards');
      return;
    }

    setError('');
    setStep('generating');

    try {
      const topicContent = formData.useTopicContent && selectedTopic 
        ? `${selectedTopic.description}\n\n${selectedTopic.modules.map(module => 
            `${module.title}: ${module.description}\n${module.examples.map(example => 
              `${example.title}: ${example.content} (${example.formula})`
            ).join('\n')}`
          ).join('\n\n')}`
        : null;

      const result = await deepseekApi.generateFlashCards(
        formData.topic,
        formData.numberOfCards,
        formData.language,
        formData.difficulty,
        topicContent
      );
      setFlashCards(result);
      setStep('playing');
    } catch (err) {
      console.error('DeepSeek API Error:', err);
      
      // Create educational mock flash cards based on topic content
      const isTopicBased = formData.useTopicContent && selectedTopic;
      const mockFlashCards = {
        title: formData.topic,
        language: formData.language,
        difficulty: formData.difficulty,
        totalCards: formData.numberOfCards,
        cards: isTopicBased ? [
          {
            id: 1,
            question: `什麼是「${selectedTopic.title}」的主要概念？`,
            answer: `${selectedTopic.title}是${selectedTopic.description}`,
            explanation: `理解這個概念有助於掌握相關的數學原理和應用。`,
            category: '概念理解',
            keywords: ['概念', '原理', '數學'],
            imageQuery: selectedTopic.code,
            imageUrl: `https://source.unsplash.com/800x600/?mathematics,${selectedTopic.code}`,
            imageAlt: `${selectedTopic.title}數學概念圖解`
          },
          {
            id: 2,
            question: `在「${selectedTopic.title}」中使用了哪些數學技巧？`,
            answer: `這個主題涵蓋了${selectedTopic.modules.length}個主要模組的數學技巧和方法。`,
            explanation: `掌握這些技巧有助於提高解題能力和數學思維。`,
            category: '解題技巧',
            keywords: ['技巧', '方法', '解題'],
            imageQuery: 'mathematics techniques',
            imageUrl: 'https://source.unsplash.com/800x600/?mathematics,techniques',
            imageAlt: '數學技巧圖解'
          },
          {
            id: 3,
            question: `How would you adapt this essay format for a different audience?`,
            answer: `You could change the tone, vocabulary level, and examples to match your target audience while keeping the same structure.`,
            explanation: `Learning to adapt writing for different audiences is a crucial communication skill.`,
            category: 'adaptation',
            keywords: ['adaptation', 'audience', 'communication'],
            imageQuery: 'audience communication',
            imageUrl: 'https://source.unsplash.com/800x600/?communication,audience',
            imageAlt: 'Audience communication concept'
          }
        ] : [
          {
            id: 1,
            question: `What is the main topic of "${formData.topic}"?`,
            answer: `The main topic is ${formData.topic}, which covers important concepts and ideas.`,
            explanation: `This question helps students understand the central theme and focus of the content.`,
            category: 'comprehension',
            keywords: [formData.topic.split(' ')[0], 'main topic', 'theme'],
            imageQuery: formData.topic,
            imageUrl: 'https://source.unsplash.com/800x600/?education,learning',
            imageAlt: `Educational content about ${formData.topic}`
          },
          {
            id: 2,
            question: `What are the key concepts in "${formData.topic}"?`,
            answer: `The key concepts include fundamental principles, important terminology, and practical applications.`,
            explanation: `Understanding key concepts helps students grasp the essential elements of the topic.`,
            category: 'analysis',
            keywords: ['key concepts', 'principles', 'terminology'],
            imageQuery: 'key concepts',
            imageUrl: 'https://source.unsplash.com/800x600/?concepts,study',
            imageAlt: 'Key concepts illustration'
          },
          {
            id: 3,
            question: `How can you apply knowledge of "${formData.topic}" in practice?`,
            answer: `You can apply this knowledge through real-world examples, problem-solving, and practical exercises.`,
            explanation: `Application questions help students connect theoretical knowledge with practical skills.`,
            category: 'application',
            keywords: ['application', 'practice', 'real-world'],
            imageQuery: 'practical application',
            imageUrl: 'https://source.unsplash.com/800x600/?application,practice',
            imageAlt: 'Practical application concept'
          }
        ]
      };
      
      console.log('Using mock flash cards due to API error');
      setFlashCards(mockFlashCards);
      setStep('playing');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveCard = (card) => {
    const cardId = card.id || flashCardStorage.generateCardId(card);
    
    if (savedCards.has(cardId)) {
      flashCardStorage.removeSavedCard(cardId);
      setSavedCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(cardId);
        return newSet;
      });
    } else {
      flashCardStorage.saveCard(card, flashCards);
      setSavedCards(prev => new Set([...prev, cardId]));
    }
  };

  const handleSaveEntireSet = () => {
    if (flashCards) {
      const setId = flashCardStorage.saveFlashCardSet(flashCards);
      if (setId) {
        alert('Flash card set saved successfully!');
      } else {
        alert('Failed to save flash card set.');
      }
    }
  };

  // If AI is not available, show fallback component
  if (!useAI) {
    return <FlashCardsFallback onBack={onBack} />;
  }

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div
        className="relative rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8"
        style={{
          backgroundImage: 'linear-gradient(rgba(147, 51, 234, 0.9), rgba(219, 39, 119, 0.9)), url(https://images.unsplash.com/photo-1606092195730-5d7b9af1efc5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <Brain className="w-8 h-8" />
            <h2 className="text-3xl font-bold">AI Flash Cards Generator</h2>
          </div>
          <p className="text-purple-100 text-lg">
            Create personalized flash cards on any topic using AI technology
          </p>
        </div>
      </div>

      {step === 'setup' && (
        <SetupForm
          formData={formData}
          onInputChange={handleInputChange}
          onGenerate={handleGenerate}
          onBack={onBack}
          error={error}
          onUseSamples={() => setUseAI(false)}
          selectedTopic={selectedTopic}
        />
      )}

      {step === 'generating' && (
        <GeneratingView topic={formData.topic} />
      )}

      {step === 'playing' && flashCards && (
        <FlashCardsPlayer
          flashCards={flashCards}
          onBack={() => setStep('setup')}
          onComplete={() => setStep('results')}
          onSaveCard={handleSaveCard}
          savedCards={savedCards}
          onSaveSet={handleSaveEntireSet}
        />
      )}

      {step === 'results' && flashCards && (
        <ResultsSummary
          flashCards={flashCards}
          onRestart={() => setStep('setup')}
          onPlayAgain={() => setStep('playing')}
        />
      )}
    </div>
  );
};

const SetupForm = ({ formData, onInputChange, onGenerate, onBack, error, onUseSamples, selectedTopic }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Create Flash Cards</h3>
        <button
          onClick={onBack}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          ← Back to Templates
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-red-700 mb-3">{error}</div>
          <button
            onClick={onUseSamples}
            className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded hover:bg-red-200 transition-colors"
          >
            Try Sample Flash Cards Instead
          </button>
        </div>
      )}

      {selectedTopic && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <h4 className="font-medium text-blue-900">從數學課題生成</h4>
          </div>
          <p className="text-sm text-blue-700 mb-3">
            基於數學課題創建閃卡：<strong>"{selectedTopic.title}"</strong>
          </p>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.useTopicContent}
              onChange={(e) => onInputChange('useTopicContent', e.target.checked)}
              className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-blue-700">使用課題內容生成相關問題</span>
          </label>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Topic *
          </label>
          <input
            type="text"
            value={formData.topic}
            onChange={(e) => onInputChange('topic', e.target.value)}
            placeholder="e.g., European Geography, Biology Basics, Spanish Vocabulary"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Number of Cards
          </label>
          <select
            value={formData.numberOfCards}
            onChange={(e) => onInputChange('numberOfCards', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {[3, 5, 8, 10, 15, 20].map(num => (
              <option key={num} value={num}>{num} cards</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Language
          </label>
          <select
            value={formData.language}
            onChange={(e) => onInputChange('language', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {flashCardLanguages.map(lang => (
              <option key={lang.id} value={lang.name}>{lang.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Difficulty Level
          </label>
          <select
            value={formData.difficulty}
            onChange={(e) => onInputChange('difficulty', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {flashCardDifficulties.map(diff => (
              <option key={diff.id} value={diff.id}>
                {diff.name} - {diff.description}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={onGenerate}
          className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium"
        >
          <Brain className="w-4 h-4" />
          Generate Flash Cards
        </button>
      </div>
    </div>
  );
};

const GeneratingView = ({ topic }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
      <div className="animate-spin w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full mx-auto mb-4"></div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        Generating Flash Cards
      </h3>
      <p className="text-gray-600">
        Creating personalized flash cards about "{topic}"...
      </p>
      <p className="text-sm text-gray-500 mt-2">
        This may take a few moments as our AI crafts the perfect learning experience for you.
      </p>
    </div>
  );
};

const FlashCardsPlayer = ({ flashCards, onBack, onComplete, onSaveCard, savedCards, onSaveSet }) => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoAdvance, setAutoAdvance] = useState(false);

  const currentCard = flashCards.cards[currentCardIndex];
  const isLastCard = currentCardIndex === flashCards.cards.length - 1;
  const progress = ((currentCardIndex + 1) / flashCards.cards.length) * 100;

  const speakText = useCallback((text) => {
    if (soundEnabled) {
      deepseekApi.speakTextEnhanced(text, flashCards.language);
    }
  }, [soundEnabled, flashCards.language]);

  const handleNext = () => {
    if (isLastCard) {
      onComplete();
    } else {
      setCurrentCardIndex(prev => prev + 1);
      setShowAnswer(false);
    }
  };

  const handlePrevious = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(prev => prev - 1);
      setShowAnswer(false);
    }
  };

  const handleShowAnswer = () => {
    setShowAnswer(true);
    if (autoAdvance) {
      setTimeout(() => {
        handleNext();
      }, flashCardSettings.autoAdvanceDelay);
    }
  };

  React.useEffect(() => {
    if (currentCard && soundEnabled) {
      speakText(currentCard.question);
    }
  }, [currentCardIndex, currentCard, soundEnabled, speakText]);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← Back to Setup
            </button>
            <div className="text-sm text-gray-600">
              Card {currentCardIndex + 1} of {flashCards.cards.length}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-2 rounded-lg transition-colors ${
                soundEnabled 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            
            <button
              onClick={() => setAutoAdvance(!autoAdvance)}
              className={`p-2 rounded-lg transition-colors ${
                autoAdvance 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              <Play className="w-4 h-4" />
            </button>
            
            <button
              onClick={onSaveSet}
              className="p-2 rounded-lg bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
              title="Save entire flash card set"
            >
              <Save className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Flash Card */}
      <FlashCardWithAnimation
        card={currentCard}
        showAnswer={showAnswer}
        onShowAnswer={handleShowAnswer}
        onSpeak={speakText}
        soundEnabled={soundEnabled}
        onSave={onSaveCard}
        isSaved={savedCards.has(currentCard.id || flashCardStorage.generateCardId(currentCard))}
      />

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={handlePrevious}
          disabled={currentCardIndex === 0}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>
        
        {showAnswer && (
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            {isLastCard ? 'Complete' : 'Next Card'}
          </button>
        )}
      </div>
    </div>
  );
};

const ResultsSummary = ({ flashCards, onRestart, onPlayAgain }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
      <div className="mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Brain className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Congratulations!
        </h3>
        <p className="text-gray-600">
          You've completed all {flashCards.totalCards} flash cards about "{flashCards.title}"
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">{flashCards.totalCards}</div>
          <div className="text-sm text-gray-500">Cards Completed</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{flashCards.language}</div>
          <div className="text-sm text-gray-500">Language</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{flashCards.difficulty}</div>
          <div className="text-sm text-gray-500">Difficulty</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600">100%</div>
          <div className="text-sm text-gray-500">Progress</div>
        </div>
      </div>

      <div className="flex gap-4 justify-center">
        <button
          onClick={onPlayAgain}
          className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Play Again
        </button>
        <button
          onClick={onRestart}
          className="flex items-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Brain className="w-4 h-4" />
          Create New Set
        </button>
      </div>
    </div>
  );
};

export default FlashCardsGenerator; 